import React from 'react';
import { Order, OrderItem, OrderDiscount, OrderTax } from '../../types';
import { Paper, Typography, Chip, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Grid, useTheme } from '@mui/material';
import {
  getOrderStatusColor,
  getOrderStatusText,
  formatDateTime,
  formatCurrency,
  mapCloudStateToOrderStatus
} from '../../utils/helpers';
import { useAppSelector } from '../../hooks/redux';

interface OrderDetailsViewProps {
  order: Order | null;
  showHeader?: boolean; // Whether to show the header section
}

// Helper to display N/A for undefined or empty strings
const displayValue = (value: string | undefined | null, prefix = '', suffix = '') => {
  return value ? `${prefix}${value}${suffix}` : 'N/A';
};

const OrderDetailsView: React.FC<OrderDetailsViewProps> = ({ order, showHeader = true }) => {
  const theme = useTheme();
  
  // Use the latest order data from the store, fallback to passed order
  const currentOrder = useAppSelector((state) => {
    if (!order?.id) return order;
    return state.orders.orderHistory?.find(o => o.id === order.id) ||
      state.orders.newOrders?.find(o => o.id === order.id) ||
      order;
  });

  if (!currentOrder) {
    return (
      <Box sx={{ 
        p: 3, 
        backgroundColor: theme.palette.background.default, 
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="h6" color="text.secondary" align="center">
          Select an order to view its details.
        </Typography>
      </Box>
    );
  }  return (
    <Box sx={{ backgroundColor: theme.palette.background.paper }}>
      {/* Header Section */}
      {showHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: `2px solid ${theme.palette.primary.main}` }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Order Details - {currentOrder.cloudShortCode}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
              Cloud State:
            </Typography>
            <Chip
              label={currentOrder.cloudOrderState || 'N/A'}
              size="small"
              color={currentOrder.cloudOrderState ? getOrderStatusColor(mapCloudStateToOrderStatus(currentOrder.cloudOrderState) || currentOrder.status) : 'default'}
              sx={{ color: 'white', fontWeight: 'bold' }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Order Information Section */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Box sx={{ 
            p: 2, 
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 1, 
            backgroundColor: theme.palette.background.default, 
            height: '100%' 
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>
              Order Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={1}>              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Cloud Order ID:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{currentOrder.cloudOrderId}</Typography></Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Local Order ID:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{displayValue(currentOrder.localOrderId)}</Typography></Grid>              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Local Order #:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{displayValue(currentOrder.localOrderNumber)}</Typography></Grid>            
              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Branch:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{currentOrder.branch?.name || currentOrder.branchId}</Typography></Grid>             
              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>POS Status:</Typography></Grid>
              <Grid size={{ xs: 7 }}>
                <Chip
                  label={getOrderStatusText(currentOrder.status)}
                  color={getOrderStatusColor(currentOrder.status)}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ fontWeight: 'bold' }}
                />
              </Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Order Time:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{formatDateTime(currentOrder.orderTime)}</Typography></Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Processed Time:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{currentOrder.processedTime ? formatDateTime(currentOrder.processedTime) : 'N/A'}</Typography></Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Last Updated:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{currentOrder.lastUpdated ? formatDateTime(currentOrder.lastUpdated) : 'N/A'}</Typography></Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Items:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{currentOrder.totalItems}</Typography></Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Amount:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1" sx={{ color: theme.palette.warning.main, fontWeight: 'bold' }}>{formatCurrency(currentOrder.total)}</Typography></Grid>

              {currentOrder.notes && (
                <>
                  <Grid size={{ xs: 12 }}><Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>Notes:</Typography></Grid>
                  <Grid size={{ xs: 12 }}><Typography variant="body1" sx={{ fontStyle: 'italic' }}>{currentOrder.notes}</Typography></Grid>
                </>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Customer & Payment Section - Using Grid for MUI v7 */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={{ 
            p: 2, 
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 1, 
            backgroundColor: theme.palette.background.default, 
            mb: 2 
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>
              Customer Information
            </Typography>
            <Divider sx={{ mb: 2 }} />            <Grid container spacing={1}>
              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Name:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{displayValue(currentOrder.customerName)}</Typography></Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Phone:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{displayValue(currentOrder.customerPhone)}</Typography></Grid>

              {/* order.customerAddress is not in the Order interface based on provided types */}
              {/* If it were, it would be: 
              {order.customerAddress && (
                <>
                  <Grid size={{ xs: 12 }}><Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>Address:</Typography></Grid>
                  <Grid size={{ xs: 12 }}><Typography variant="body1">{order.customerAddress}</Typography></Grid>
                </>
              )} 
              */}
            </Grid>
          </Box>
          <Box sx={{ 
            p: 2, 
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 1, 
            backgroundColor: theme.palette.background.default 
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>
              Payment Information
            </Typography>
            <Divider sx={{ mb: 2 }} />            <Grid container spacing={1}>
              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Method:</Typography></Grid>
              <Grid size={{ xs: 7 }}><Typography variant="body1">{displayValue(currentOrder.paymentMethod)}</Typography></Grid>

              <Grid size={{ xs: 5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Status:</Typography></Grid>              <Grid size={{ xs: 7 }}>
                <Chip
                  label={currentOrder.isPaid ? 'Paid' : 'Not Paid'}
                  color={currentOrder.isPaid ? 'success' : 'warning'}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>        {/* Delivery/Pickup Information Section - Using Grid for MUI v7 */}
        {(currentOrder.deliveryDetails || currentOrder.pickupTime || currentOrder.dispatchedAt || currentOrder.deliveredAt) && (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ 
              p: 2, 
              mt: 1, 
              border: `1px solid ${theme.palette.divider}`, 
              borderRadius: 1, 
              backgroundColor: theme.palette.background.default 
            }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>
                Delivery/Pickup Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={1}>
                {currentOrder.deliveryDetails && (
                  <>
                    <Grid size={{ xs: 12 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivery Details:</Typography></Grid>
                    <Grid size={{ xs: 12 }}><Typography variant="body1">{currentOrder.deliveryDetails}</Typography></Grid>
                  </>
                )}
                {currentOrder.pickupTime && (
                  <>
                    <Grid size={{ xs: 4 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Pickup Time:</Typography></Grid>
                    <Grid size={{ xs: 8 }}><Typography variant="body1">{formatDateTime(currentOrder.pickupTime)}</Typography></Grid>
                  </>
                )}
                {currentOrder.dispatchedAt && (
                  <>
                    <Grid size={{ xs: 4 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Dispatched At:</Typography></Grid>
                    <Grid size={{ xs: 8 }}><Typography variant="body1">{formatDateTime(currentOrder.dispatchedAt)}</Typography></Grid>
                  </>
                )}
                {currentOrder.deliveredAt && (
                  <>
                    <Grid size={{ xs: 4 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivered At:</Typography></Grid>
                    <Grid size={{ xs: 8 }}><Typography variant="body1">{formatDateTime(currentOrder.deliveredAt)}</Typography></Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Grid>
        )}        {/* Order Items Section - Using Grid for MUI v7 */}
        <Grid size={{ xs: 12 }}>          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium', mt: 2 }}>
          Order Items ({currentOrder.items.length})
        </Typography>
          <TableContainer component={Paper} sx={{ border: `1px solid ${theme.palette.divider}`, maxHeight: 'none' }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Price</TableCell>
                </TableRow>
              </TableHead>              <TableBody>
                {currentOrder.items.map((item: OrderItem) => (
                  <React.Fragment key={item.id}>
                    <TableRow hover>                      <TableCell component="th" scope="row">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.name}
                        </Typography>
                        {item.localMenuItemId && (
                          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            POS ID: {item.localMenuItemId}
                          </Typography>
                        )}
                        {item.notes && (
                          <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 0.5 }}>
                            Note: {item.notes}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'medium' }}>{formatCurrency(item.quantity * item.price)}</TableCell>
                    </TableRow>
                    {/* Display modifiers (condiments) */}
                    {item.modifiers && item.modifiers.length > 0 && item.modifiers.map((modifier) => (
                      <TableRow key={`${item.id}-modifier-${modifier.id}`} sx={{ backgroundColor: theme.palette.action.selected }}>                        <TableCell component="th" scope="row" sx={{ pl: 4 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label="Condiment"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: '20px' }}
                          />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {modifier.name}
                            </Typography>
                            {modifier.localMenuItemId && (
                              <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                                POS ID: {modifier.localMenuItemId}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{modifier.quantity}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatCurrency(modifier.price)}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatCurrency(modifier.quantity * modifier.price)}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>        {/* Discounts Section - Using Grid for MUI v7 */}
        {currentOrder.discounts && currentOrder.discounts.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ 
              p: 2, 
              mt: 1, 
              border: `1px solid ${theme.palette.divider}`, 
              borderRadius: 1, 
              backgroundColor: theme.palette.background.default, 
              height: '100%' 
            }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>                Discounts Applied ({currentOrder.discounts.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'none' }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Discount Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentOrder.discounts.map((discount: OrderDiscount) => (
                      <TableRow key={discount.id}>
                        <TableCell>{discount.name}</TableCell>
                        <TableCell align="right">-{formatCurrency(discount.value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        )}        {/* Taxes Section - Using Grid for MUI v7 */}
        {currentOrder.taxes && currentOrder.taxes.length > 0 && (
          <Grid size={{ xs: 12, md: currentOrder.discounts && currentOrder.discounts.length > 0 ? 6 : 12 }}>
            <Box sx={{ 
              p: 2, 
              mt: 1, 
              border: `1px solid ${theme.palette.divider}`, 
              borderRadius: 1, 
              backgroundColor: theme.palette.background.default, 
              height: '100%' 
            }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>                Taxes ({currentOrder.taxes.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'none' }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tax Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Value</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Included in Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentOrder.taxes.map((tax: OrderTax) => (
                      <TableRow key={tax.id}>
                        <TableCell>{tax.name}</TableCell>
                        <TableCell align="right">{formatCurrency(tax.value)}</TableCell>
                        <TableCell>{tax.includedInPrice ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>)}        {/* Log Messages Section */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ 
            p: 2, 
            mt: 1, 
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: 1, 
            backgroundColor: theme.palette.background.default 
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>
              Log Messages
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {currentOrder.errorMessage ? (
              <Box sx={{ 
                p: 2, 
                border: `1px solid ${theme.palette.error.main}`, 
                borderRadius: 1, 
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : '#ffebee' 
              }}>
                <Typography variant="body2" sx={{ 
                  fontFamily: 'monospace', 
                  color: theme.palette.error.main, 
                  whiteSpace: 'pre-wrap' 
                }}>
                  {currentOrder.errorMessage}
                </Typography>
                {currentOrder.lastUpdated && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                    Last updated: {formatDateTime(currentOrder.lastUpdated)}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ 
                p: 2, 
                border: `1px solid ${theme.palette.divider}`, 
                borderRadius: 1, 
                backgroundColor: theme.palette.action.hover 
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No log messages available for this order.
                </Typography>
              </Box>
            )}
          </Box>        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetailsView;
